import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { api } from '../services/api'
import styles from './CreateEventScreen.module.css'

type TicketingMode = 'sell' | 'external' | 'free'

export function CreateEventScreen() {
  const navigate = useNavigate()
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const [formData, setFormData] = useState({
    title: '',
    description: '',
    event_date: '',
    venue_name: '',
    venue_address: '',
    kind: 'music' as string,
    genre: 'Music' as string,
    price: 'Free',
    ticketing_mode: 'sell' as TicketingMode,
    ticket_url: '',
    is_curated: false,
  })

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setLoading(true)
    setError(null)

    try {
      // datetime-local gives naive wall-clock in the browser's local TZ.
      // Convert to a UTC ISO instant before sending (backend stores TIMESTAMPTZ).
      const payload = {
        ...formData,
        event_date: formData.event_date ? new Date(formData.event_date).toISOString() : '',
      }
      await api.events.create(payload)
      navigate('/')
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create event')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className={styles.container}>
      <h2 className={styles.header}>Post an event</h2>

      {error && (
        <div className={styles.errorBanner}>
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit} className={styles.form}>
        <div>
          <label htmlFor="event-title" className={styles.formLabel}>Title</label>
          <input
            id="event-title"
            type="text"
            required
            value={formData.title}
            onChange={(e) => setFormData({ ...formData, title: e.target.value })}
            className={styles.formInput}
            placeholder="Event name"
          />
        </div>

        <div>
          <label htmlFor="event-description" className={styles.formLabel}>Description</label>
          <textarea
            id="event-description"
            required
            value={formData.description}
            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            className={styles.formTextarea}
            placeholder="What's happening? Who's playing? Any good-to-know facts?"
          />
        </div>

        <div>
          <label htmlFor="event-date" className={styles.formLabel}>Date & Time</label>
          <input
            id="event-date"
            type="datetime-local"
            required
            value={formData.event_date}
            onChange={(e) => setFormData({ ...formData, event_date: e.target.value })}
            className={styles.formInput}
          />
        </div>

        <div>
          <label htmlFor="event-venue-name" className={styles.formLabel}>Venue Name</label>
          <input
            id="event-venue-name"
            type="text"
            required
            value={formData.venue_name}
            onChange={(e) => setFormData({ ...formData, venue_name: e.target.value })}
            className={styles.formInput}
            placeholder="Where is it?"
          />
        </div>

        <div>
          <label htmlFor="event-venue-address" className={styles.formLabel}>Venue Address</label>
          <input
            id="event-venue-address"
            type="text"
            value={formData.venue_address}
            onChange={(e) => setFormData({ ...formData, venue_address: e.target.value })}
            className={styles.formInput}
            placeholder="Full address (optional)"
          />
        </div>

        <div>
          <label htmlFor="event-kind" className={styles.formLabel}>Event Type</label>
          <select
            id="event-kind"
            value={formData.kind}
            onChange={(e) => setFormData({ ...formData, kind: e.target.value as string })}
            className={styles.formInput}
          >
            <option value="music">Music</option>
            <option value="market">Market</option>
            <option value="community">Community</option>
            <option value="spoken_word">Spoken Word</option>
          </select>
        </div>

        <div>
          <label htmlFor="event-genre" className={styles.formLabel}>Genre</label>
          <input
            id="event-genre"
            type="text"
            value={formData.genre}
            onChange={(e) => setFormData({ ...formData, genre: e.target.value })}
            className={styles.formInput}
            placeholder="e.g., House, Techno, Folk"
          />
        </div>

        <div>
          <label htmlFor="event-price" className={styles.formLabel}>Price</label>
          <input
            id="event-price"
            type="text"
            value={formData.price}
            onChange={(e) => setFormData({ ...formData, price: e.target.value })}
            className={styles.formInput}
            placeholder="Free or e.g. $15"
          />
        </div>

        <div>
          <label className={styles.formLabel}>Ticketing</label>
          <div className={styles.radioGroup}>
            {[
              { value: 'sell' as TicketingMode, label: 'Sell (integrated)' },
              { value: 'external' as TicketingMode, label: 'External link' },
              { value: 'free' as TicketingMode, label: 'Free / RSVP' },
            ].map((mode) => (
              <label key={mode.value} className={styles.radioLabel}>
                <input
                  type="radio"
                  name="ticketing_mode"
                  value={mode.value}
                  checked={formData.ticketing_mode === mode.value}
                  onChange={(e) => setFormData({ ...formData, ticketing_mode: e.target.value as TicketingMode })}
                />
                {mode.label}
              </label>
            ))}
          </div>
        </div>

        {formData.ticketing_mode === 'external' && (
          <div>
            <label htmlFor="event-ticket-url" className={styles.formLabel}>Ticket URL</label>
            <input
              id="event-ticket-url"
              type="url"
              required={formData.ticketing_mode === 'external'}
              value={formData.ticket_url}
              onChange={(e) => setFormData({ ...formData, ticket_url: e.target.value })}
              className={styles.formInput}
              placeholder="https://..."
            />
          </div>
        )}

        <div className={styles.checkboxRow}>
          <input
            type="checkbox"
            id="tips_curated"
            checked={formData.is_curated}
            onChange={(e) => setFormData({ ...formData, is_curated: e.target.checked })}
          />
          <label htmlFor="tips_curated" className={styles.checkboxLabel}>
            This event gets curated by Tips before listing
          </label>
        </div>

        <button
          type="submit"
          disabled={loading}
          className={styles.submitButton}
        >
          {loading ? 'Posting...' : 'Post event'}
        </button>
      </form>
    </div>
  )
}
